import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Popconfirm, Image } from 'components/shared/AntComponent';
import { draftExam, submitExam } from 'services/store/actions/examAction';
import kosoftNew from 'assets/img/kosoftnewlogo.png';
import Logo from 'assets/img/TVS-TS---Logo.png';

const IS_PRODUCTION = process.env.NODE_ENV === 'production' || new URLSearchParams(window.location.search).get('enforce') === '1';

const navBtnStyle = (type: 'outline' | 'review' | 'submit' | 'primary'): React.CSSProperties => {
    const base: React.CSSProperties = {
        padding: '9px 14px', fontSize: '13px', fontWeight: 600,
        borderRadius: '7px', cursor: 'pointer', transition: 'all 0.2s',
        whiteSpace: 'nowrap', letterSpacing: '0.01em',
        flex: '0 0 auto'
    };
    switch (type) {
        case 'outline': return { ...base, border: '2px solid #3B6E91', background: '#fff', color: '#3B6E91' };
        case 'review':  return { ...base, border: '2px solid #faad14', background: '#fff', color: '#faad14', flex: '1 1 0', maxWidth: '160px', textAlign: 'center' };
        case 'submit':  return { ...base, border: 'none', background: '#52c41a', color: '#fff', boxShadow: '0 3px 10px rgba(82,196,26,0.3)' };
        case 'primary': return { ...base, border: 'none', background: '#3B6E91', color: '#fff', boxShadow: '0 3px 10px rgba(24,144,255,0.25)' };
    }
};

const STATUS_COLOR: Record<string, string> = {
    current: '#3B6E91', answered: '#52c41a', review: '#faad14', 'not-visited': '#f0f0f0'
};
const STATUS_TEXT: Record<string, string> = {
    current: '#fff', answered: '#fff', review: '#fff', 'not-visited': '#555'
};

interface PaletteContentProps {
    guestInfo: any;
    timeLeft: number | null;
    isLastFiveMin: boolean;
    t: { h: string; m: string; s: string };
    answeredQuestions: Set<number>;
    reviewQuestions: Set<number>;
    allQuestionsWithNumbers: any[];
    groupedQuestionsWithNumbers: Record<string, any>;
    expandedSection: string | null;
    setExpandedSection: React.Dispatch<React.SetStateAction<string | null>>;
    expandedSubject: string | null;
    setExpandedSubject: React.Dispatch<React.SetStateAction<string | null>>;
    currentQuestionId: number | undefined;
    handleQuestionClick: (questionId: number) => void;
}



const ConductExam = () => {
    const dispatch = useDispatch();
    const history  = useNavigate();
    const tenantId = useSelector((state: any) => state.tenant.tenantId);

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answeredQuestions, setAnsweredQuestions] = useState(new Set<number>());
    const [reviewQuestions, setReviewQuestions]     = useState(new Set<number>());
    const [selectedAnswers, setSelectedAnswers]     = useState<Record<number, any>>({});
    const [expandedSection, setExpandedSection]     = useState<string | null>(null);
    const [expandedSubject, setExpandedSubject]     = useState<string | null>(null);

    const data: any = useMemo(() => {
        const raw = localStorage.getItem('sectionData');
        return raw ? JSON.parse(raw) : null;
    }, []);
    const guestInfo: any = useMemo(() => {
        const raw = localStorage.getItem('guestInfoData');
        return raw ? JSON.parse(raw) : null;
    }, []);

    const { success5, error5 } = useSelector((state: any) => state.exam);
    const [showSuccessmsg, setShowSuccessmsg] = useState(false);
    const [showErrormsg, setShowErrorMsg]     = useState(false);
    const successmsg = showSuccessmsg ? success5 : false;
    const errormsg   = showErrormsg   ? error5   : false;

    const [timeLeft, setTimeLeft]     = useState<number | null>(null);
    const displayTime                 = timeLeft ?? 0;
    const timerStartedRef             = useRef(false);
    const isLastFiveMin               = displayTime > 0 && displayTime <= 300;

    const [isPaletteOpen, setIsPaletteOpen]       = useState(false);
    const [showStartWarning, setShowStartWarning] = useState(true);
    const [showViolationWarning, setShowViolationWarning] = useState(false);
    const [markReviewFlash, setMarkReviewFlash]   = useState(false);

    const hasAutoSubmittedRef = useRef(false);
    const handleSubmitRef     = useRef<() => void>(() => {});

    const { examStartDateTime, examEndDateTime } = useMemo(() => {
        if (!guestInfo?.examDetails?.examDate || !guestInfo?.examDetails?.examSlots?.[0]) {
            return { examStartDateTime: null, examEndDateTime: null };
        }
        const dateOnly = guestInfo.examDetails.examDate.split('T')[0];
        const slot = guestInfo.examDetails.examSlots[0];
        return {
            examStartDateTime: new Date(`${dateOnly}T${slot.examStartTime}`),
            examEndDateTime:   new Date(`${dateOnly}T${slot.examEndTime}`)
        };
    }, [guestInfo]);

    useEffect(() => {
        if (!examStartDateTime || !examEndDateTime) return;
        const tick = () => {
            const remaining = Math.max(Math.floor((examEndDateTime.getTime() - Date.now()) / 1000), 0);
            setTimeLeft(remaining);
            if (remaining > 0) timerStartedRef.current = true;
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [examStartDateTime, examEndDateTime]);

    useEffect(() => {
        if (timeLeft !== null && timerStartedRef.current && timeLeft === 0 && !hasAutoSubmittedRef.current) {
            hasAutoSubmittedRef.current = true;
            handleSubmitRef.current();
        }
    }, [timeLeft]);

    const allQuestions = useMemo(() => {
        const qs: any[] = [];
        const keyOrder = ['aptitude', 'technical'];
        keyOrder.forEach(k => { if (data?.[k] && Array.isArray(data[k])) qs.push(...data[k]); });
        Object.keys(data || {}).forEach(k => {
            if (!keyOrder.includes(k) && Array.isArray(data[k])) qs.push(...data[k]);
        });
        return qs;
    }, [data]);

    const groupedQuestionsWithNumbers = useMemo(() => {
        if (!data || typeof data !== 'object') return {};
        const sections: any = {};
        allQuestions.forEach((q: any) => {
            const sn  = q.qpSection   || 'Unknown Section';
            const sub = q.subjectName || 'Unknown Subject';
            if (!sections[sn])      sections[sn] = {};
            if (!sections[sn][sub]) sections[sn][sub] = [];
            sections[sn][sub].push(q);
        });
        let no = 1;
        Object.keys(sections).sort().forEach(sn => {
            Object.keys(sections[sn]).forEach(sub => {
                sections[sn][sub] = sections[sn][sub]
                    .sort((a: any, b: any) => a.questionId - b.questionId)
                    .map((q: any) => ({ ...q, questionNo: no++ }));
            });
        });
        return sections;
    }, [allQuestions]);

    const PaletteContent = React.memo(function PaletteContent({
    guestInfo, isLastFiveMin, t,
    answeredQuestions, reviewQuestions, allQuestionsWithNumbers,
    groupedQuestionsWithNumbers, expandedSection, setExpandedSection,
    expandedSubject, setExpandedSubject, currentQuestionId, handleQuestionClick
}: PaletteContentProps) {

    const getStatus = (questionId: number) => {
        if (currentQuestionId === questionId) return 'current';
        if (answeredQuestions.has(questionId)) return 'answered';
        if (reviewQuestions.has(questionId))   return 'review';
        return 'not-visited';
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

            <div style={{ padding: '16px', textAlign: 'center', borderBottom: '1px solid #f0f0f0' }}>
                                <img src={tenantId === 1153 ? kosoftNew : Logo} alt="kovinalogo" height="40px" />
               
            </div>

            <div style={{ padding: '14px 12px 12px', textAlign: 'center', background: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: '#aaa', letterSpacing: '0.1em', marginBottom: '10px', textTransform: 'uppercase' }}>
                    Time Remaining
                </div>
               <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '6px',
    animation: isLastFiveMin ? 'timerBlink 1s ease-in-out infinite' : 'none'
}}>
    {[{ val: t.h, label: 'HRS' }, { val: t.m, label: 'MIN' }, { val: t.s, label: 'SEC' }].map(({ val, label }, i) => (
        <React.Fragment key={label}>
            {i > 0 && (
                <div style={{
                    color: isLastFiveMin ? '#ff4d4f' : '#3B6E91',
                    fontWeight: 700,
                    fontSize: '18px',
                    paddingBottom: '14px',
                    width: '10px',            
                    textAlign: 'center'
                }}>
                    :
                </div>
            )}

            <div style={{
                background: isLastFiveMin ? '#fff1f0' : '#fff',
                border: `2px solid ${isLastFiveMin ? '#ff4d4f' : '#d9d9d9'}`,
                borderRadius: '8px',
                padding: '6px 8px',

                width: '52px',              
                textAlign: 'center',
                flexShrink: 0               
            }}>
                <div style={{
                    fontSize: '22px',
                    fontWeight: 700,
                    color: isLastFiveMin ? '#ff4d4f' : '#3B6E91',
                    lineHeight: 1.1,

                    fontFamily: 'monospace',       
                    fontVariantNumeric: 'tabular-nums'
                }}>
                    {val}
                </div>

                <div style={{
                    fontSize: '9px',
                    color: '#bbb',
                    marginTop: '2px',
                    letterSpacing: '0.05em'
                }}>
                    {label}
                </div>
            </div>
        </React.Fragment>
    ))}
</div>
                {isLastFiveMin && (
                    <div style={{ marginTop: '8px', fontSize: '11px', color: '#ff4d4f', fontWeight: 600 }}>
                        ⚠ Less than 5 minutes remaining!
                    </div>
                )}
            </div>

            <div style={{ padding: '12px 14px 8px', borderBottom: '1px solid #f0f0f0' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: '#888', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Question Palette
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {[
                        { color: '#3B6E91', label: 'Current' },
                        { color: '#52c41a', label: 'Answered' },
                        { color: '#faad14', label: 'Review' },
                        { color: '#f0f0f0', label: 'Not Visited', border: '1px solid #ddd' },
                    ].map(({ color, label, border }) => (
                        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#555' }}>
                            <div style={{ width: '13px', height: '13px', background: color, borderRadius: '3px', border: border || 'none' }} />
                            {label}
                        </div>
                    ))}
                </div>
            </div>

            <div style={{ padding: '8px 14px', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: '12px', fontSize: '12px' }}>
                <span style={{ color: '#52c41a', fontWeight: 600 }}>✓ {answeredQuestions.size} Answered</span>
                <span style={{ color: '#faad14', fontWeight: 600 }}>⊙ {reviewQuestions.size} Review</span>
                <span style={{ color: '#aaa' }}>{allQuestionsWithNumbers.length - answeredQuestions.size - reviewQuestions.size} Left</span>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
                {Object.entries(groupedQuestionsWithNumbers).map(([sn, subjects]: any) => {
                    const secOpen = expandedSection === sn;
                    return (
                        <div key={sn} style={{ marginBottom: '6px', border: '1px solid #e8e8e8', borderRadius: '8px', overflow: 'hidden' }}>
                            <div
                                onClick={() => {
                                    setExpandedSection(prev => prev === sn ? null : sn);
                                    if (expandedSection === sn) setExpandedSubject(null);
                                }}
                                style={{
                                    padding: '10px 14px', background: secOpen ? '#e6f4ff' : '#f5f5f5',
                                    cursor: 'pointer', display: 'flex', justifyContent: 'space-between',
                                    alignItems: 'center', userSelect: 'none', transition: 'background 0.2s'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{
                                        fontSize: '10px', color: '#3B6E91',
                                        display: 'inline-block', transition: 'transform 0.2s',
                                        transform: secOpen ? 'rotate(90deg)' : 'rotate(0deg)'
                                    }}>▶</span>
                                    <strong style={{ fontSize: '13px', color: '#333' }}>{sn}</strong>
                                </div>
                            </div>

                            {secOpen && Object.entries(subjects).map(([sub, questions]: any) => {
                                const subKey  = `${sn}-${sub}`;
                                const subOpen = expandedSubject === subKey;
                                return (
                                    <div key={subKey} style={{ borderTop: '1px solid #f0f0f0' }}>
                                        <div
                                            onClick={() => setExpandedSubject(prev => prev === subKey ? null : subKey)}
                                            style={{
                                                padding: '9px 14px 9px 28px',
                                                background: subOpen ? '#f0f8ff' : '#fafafa',
                                                cursor: 'pointer', display: 'flex',
                                                justifyContent: 'space-between', alignItems: 'center', userSelect: 'none'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span style={{
                                                    fontSize: '9px', color: '#aaa',
                                                    display: 'inline-block', transition: 'transform 0.2s',
                                                    transform: subOpen ? 'rotate(90deg)' : 'rotate(0deg)'
                                                }}>▶</span>
                                                <span style={{ fontSize: '12px', color: '#555' }}>{sub}</span>
                                            </div>
                                            <span style={{ background: '#52c41a', color: '#fff', padding: '1px 7px', borderRadius: '10px', fontSize: '10px' }}>
                                                {questions.length}
                                            </span>
                                        </div>

                                        {subOpen && (
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(36px,1fr))', gap: '6px', padding: '10px 12px' }}>
                                                {[...questions].sort((a: any, b: any) => a.questionNo - b.questionNo).map((q: any) => {
                                                    const status    = getStatus(q.questionId);
                                                    const isCurrent = status === 'current';
                                                    return (
                                                        <div
                                                            key={q.questionId}
                                                            onClick={() => handleQuestionClick(q.questionId)}
                                                            style={{
                                                                width: '36px', height: '36px',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                background: STATUS_COLOR[status],
                                                                color: STATUS_TEXT[status],
                                                                borderRadius: '6px', cursor: 'pointer',
                                                                fontSize: '12px', fontWeight: isCurrent ? 700 : 400,
                                                                border: status === 'not-visited' ? '1px solid #ddd' : 'none',
                                                                boxShadow: isCurrent ? '0 0 0 2px #3B6E91' : '0 1px 2px rgba(0,0,0,0.08)',
                                                                transition: 'transform 0.15s'
                                                            }}
                                                            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.12)'; }}
                                                            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                                                        >
                                                            {q.questionNo}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
});

    const allQuestionsWithNumbers = useMemo(() => {
        const qs: any[] = [];
        Object.values(groupedQuestionsWithNumbers).forEach((subjects: any) =>
            Object.values(subjects).forEach((sq: any) => qs.push(...sq))
        );
        return qs.sort((a, b) => a.questionNo - b.questionNo);
    }, [groupedQuestionsWithNumbers]);

    const currentQuestion = allQuestionsWithNumbers[currentQuestionIndex];

    useEffect(() => {
        if (!currentQuestion) return;
        const sn  = currentQuestion.qpSection   || 'Unknown Section';
        const sub = currentQuestion.subjectName || 'Unknown Subject';
        setExpandedSection(sn);
        setExpandedSubject(`${sn}-${sub}`);
    }, [currentQuestionIndex]);

    const handleNext = useCallback(() => {
        const optionId = selectedAnswers[currentQuestion?.questionId];
        if (optionId) {
            dispatch(draftExam({
                Answers: [{
                    CandExamID:         guestInfo?.candidateExam?.candExamID,
                    ExamId:             guestInfo?.examDetails?.examID,
                    TenantId:           guestInfo?.examDetails?.tenantId,
                    QuestionId:         currentQuestion?.questionId,
                    CandAnswerOptionId: optionId
                }]
            }) as any);
        }
        if (currentQuestionIndex < allQuestionsWithNumbers.length - 1)
            setCurrentQuestionIndex(i => i + 1);
    }, [selectedAnswers, currentQuestion, currentQuestionIndex, allQuestionsWithNumbers.length, guestInfo, dispatch]);

    const handlePrevious = useCallback(() => {
        if (currentQuestionIndex > 0) setCurrentQuestionIndex(i => i - 1);
    }, [currentQuestionIndex]);

    const handleAnswerSelect = useCallback((optionId: any) => {
        setSelectedAnswers(prev => ({ ...prev, [currentQuestion.questionId]: optionId }));
        setAnsweredQuestions(prev => new Set([...prev, currentQuestion.questionId]));
        setReviewQuestions(prev => {
            const s = new Set(prev); s.delete(currentQuestion.questionId); return s;
        });
    }, [currentQuestion]);

    const handleMarkForReview = useCallback(() => {
        setReviewQuestions(prev => new Set([...prev, currentQuestion.questionId]));
        setAnsweredQuestions(prev => {
            const s = new Set(prev); s.delete(currentQuestion.questionId); return s;
        });
        setMarkReviewFlash(true);
        setTimeout(() => setMarkReviewFlash(false), 500);
    }, [currentQuestion]);

    const handleQuestionClick = useCallback((questionId: number) => {
        const idx = allQuestionsWithNumbers.findIndex((q: any) => q.questionId === questionId);
        if (idx !== -1) { setCurrentQuestionIndex(idx); setIsPaletteOpen(false); }
    }, [allQuestionsWithNumbers]);

    const handleSubmit = useCallback(() => {
        const answersArray = Object.entries(selectedAnswers).map(([qId, optId]) => ({
            CandExamID:         guestInfo?.candidateExam?.candExamID,
            ExamId:             guestInfo?.examDetails?.examID,
            TenantId:           guestInfo?.examDetails?.tenantId,
            QuestionId:         parseInt(qId),
            CandAnswerOptionId: optId
        }));
        dispatch(submitExam({
            ExamId:              guestInfo?.examDetails?.examID,
            CandExamID:          guestInfo?.candidateExam?.candExamID,
            TenantId:            guestInfo?.examDetails?.tenantId,
            QuestionPaperId:     guestInfo?.candidateExam?.quesPaperID,
            AttenQuestions:      answersArray.length,
            CandidateExamStatus: 'submitted',
            Answers:             answersArray
        }) as any);
        setShowSuccessmsg(true);
        setShowErrorMsg(true);
    }, [selectedAnswers, guestInfo, dispatch]);

    useEffect(() => { handleSubmitRef.current = handleSubmit; });

    useEffect(() => {
        if (successmsg) { setShowSuccessmsg(false); history('/test-success'); }
        if (errormsg)   { setShowErrorMsg(false);   history('/test-error');   }
    }, [successmsg, errormsg]);

    useEffect(() => {
        if (!IS_PRODUCTION) return;
        const noCtx  = (e: MouseEvent)    => e.preventDefault();
        const noKeys = (e: KeyboardEvent) => {
            if (e.key === 'F12') { e.preventDefault(); return; }
            if (e.ctrlKey && e.shiftKey && ['I','C','J'].includes(e.key)) { e.preventDefault(); return; }
            if (e.ctrlKey && e.key === 'u') { e.preventDefault(); return; }
            if (e.metaKey && e.altKey && ['I','C','U'].includes(e.key))   { e.preventDefault(); return; }
        };
        document.addEventListener('contextmenu', noCtx);
        document.addEventListener('keydown', noKeys);
        return () => {
            document.removeEventListener('contextmenu', noCtx);
            document.removeEventListener('keydown', noKeys);
        };
    }, []);

    useEffect(() => {
        if (!IS_PRODUCTION) return;
        const check = () => {
            if (window.outerWidth - window.innerWidth > 160 || window.outerHeight - window.innerHeight > 160) {
                if (!hasAutoSubmittedRef.current) {
                    hasAutoSubmittedRef.current = true;
                    handleSubmitRef.current();
                }
            }
        };
        const id = setInterval(check, 1000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        if (!IS_PRODUCTION) return;
        const onHide = () => {
            if (!document.hidden || hasAutoSubmittedRef.current) return;
            hasAutoSubmittedRef.current = true;
            setShowViolationWarning(true);
            setTimeout(() => handleSubmitRef.current(), 5000);
        };
        document.addEventListener('visibilitychange', onHide);
        return () => document.removeEventListener('visibilitychange', onHide);
    }, []);

    const t = {
        h: String(Math.floor(displayTime / 3600)).padStart(2, '0'),
        m: String(Math.floor((displayTime % 3600) / 60)).padStart(2, '0'),
        s: String(displayTime % 60).padStart(2, '0'),
    };

    const paletteProps: PaletteContentProps = {
        guestInfo,
        timeLeft,
        isLastFiveMin,
        t,
        answeredQuestions,
        reviewQuestions,
        allQuestionsWithNumbers,
        groupedQuestionsWithNumbers,
        expandedSection,
        setExpandedSection,
        expandedSubject,
        setExpandedSubject,
        currentQuestionId: currentQuestion?.questionId,
        handleQuestionClick,
    };

    return (
        <div style={{ display: 'flex', height: '100vh', fontFamily: "'Segoe UI', Arial, sans-serif", overflow: 'hidden', background: '#f5f7fa' }}>

            <div className="desktop-palette" style={{
                width: '295px', flexShrink: 0,
                borderRight: '1px solid #e7e4e4',
                background: '#fff', display: 'flex', flexDirection: 'column', overflowY: 'auto'
            }}>
                <PaletteContent {...paletteProps} />
            </div>

            {isPaletteOpen && (
                <div className="mobile-only" onClick={() => setIsPaletteOpen(false)} style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000
                }} />
            )}

            <div className="mobile-only" style={{
                position: 'fixed', top: 0, left: 0,
                height: '100vh', width: '82vw', maxWidth: '310px',
                background: '#fff', zIndex: 1001, display: 'flex', flexDirection: 'column',
                transform: isPaletteOpen ? 'translateX(0)' : 'translateX(-100%)',
                transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)',
                boxShadow: isPaletteOpen ? '4px 0 20px rgba(0,0,0,0.15)' : 'none'
            }}>
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '13px 16px', background: '#3B6E91', color: '#fff', flexShrink: 0
                }}>
                    <span style={{ fontWeight: 600, fontSize: '14px' }}>Question Palette</span>
                    <button onClick={() => setIsPaletteOpen(false)} style={{
                        background: 'transparent', border: 'none', color: '#fff',
                        fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: '0 2px'
                    }}>✕</button>
                </div>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    <PaletteContent {...paletteProps} />
                </div>
            </div>

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>

               <div style={{
    background: '#3B6E91',
    height: '74px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: ' 12px',
    flexShrink: 0,
    boxShadow: '0 2px 8px rgba(24,144,255,0.2)'
}}>

    <button
        className="mobile-only"
        onClick={() => setIsPaletteOpen(true)}
        style={{
            background: 'rgba(255,255,255,0.18)',
            border: '1px solid rgba(255,255,255,0.35)',
            borderRadius: '6px',
            color: '#fff',
            fontSize: '17px',
            padding: '5px 9px',
            cursor: 'pointer'
        }}
    >
        ☰
    </button>

    <span className="desktop-only" style={{ width: '32px' }} />

    <div className="desktop-only" style={{
        fontWeight: 600,
        fontSize: '16px',
        color: 'white',
        textTransform: 'capitalize',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    }}>
        {guestInfo?.examDetails?.examName || 'Exam'}

    </div>

    <h5 className="desktop-only" style={{
        margin: 0,
        color: '#fff',
        fontWeight: 600,
        fontSize: '15px',
        flex: 1,
        textAlign: 'end',
        textTransform: 'capitalize'
    }}>
        {guestInfo?.candidate?.firstName || ''} {guestInfo?.candidate?.lastName || ''}

    </h5>

    <div className="mobile-only" style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
    }}>
        <div style={{
            fontSize: '12px',
            color: '#fff',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '120px'
        }}>
            {guestInfo?.candidate?.firstName}
        </div>

        <div style={{
            fontSize: '13px',
            fontWeight: 600,
            color: '#fff',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '140px'
        }}>
            {guestInfo?.examDetails?.examName}
        </div>
    </div>

    {/* ⏱ TIMER */}
   <div className="mobile-only" style={{
    background: isLastFiveMin ? '#ff4d4f' : 'rgba(255,255,255,0.15)',
    border: '1px solid rgba(255,255,255,0.3)',
    borderRadius: '6px',
    padding: '4px 10px',
    color: '#fff',
    fontSize: '13px',
    fontWeight: 700,

    width: '80px',              
    textAlign: 'center',
    fontVariantNumeric: 'tabular-nums', 
}}>
    {t.h}:{t.m}:{t.s}
</div>

</div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '18px 18px 8px' }}>
                    <div style={{ maxWidth: '800px', margin: '0 auto' }}>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ background: '#3B6E91', color: '#fff', borderRadius: '6px', padding: '4px 12px', fontSize: '13px', fontWeight: 600 }}>
                                    Q {currentQuestionIndex + 1} / {allQuestionsWithNumbers.length}
                                </div>
                               
                            </div>
                            <div style={{ display: 'flex', gap: '10px', fontSize: '12px' }}>
                                 <span style={{ background: '#f0f8ff', color: '#4c5d6d', border: '1px solid #3ca1f3', borderRadius: '4px', padding: '3px 10px', fontSize: '11px', fontWeight: 500 }}>
                                    {currentQuestion?.qpSection}
                                </span>
                                {/* <span style={{ color: '#52c41a', fontWeight: 600 }}>✓ {answeredQuestions.size}</span>
                                <span style={{ color: '#faad14', fontWeight: 600 }}>⊙ {reviewQuestions.size}</span> */}
                            </div>
                        </div>

                        <div style={{
                            background: '#fff', borderRadius: '10px',
                            border: '1px solid #e8e8e8', padding: '18px 20px', marginBottom: '14px',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.05)'
                        }}>
                            <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#222', margin: 0, userSelect: 'none' }}>
                                <strong style={{ color: '#3B6E91' }}>Q{currentQuestion?.questionNo}.</strong>{' '}
                                {currentQuestion?.questionText}
                            </p>
                        </div>

                        <div>
                            {currentQuestion?.exOptions?.map((option: any, idx: number) => {
                                const isSelected = selectedAnswers[currentQuestion.questionId] === option.optionId;
                                const letter = String.fromCharCode(65 + idx);
                                return (
                                    <div
                                        key={option.optionId}
                                        onClick={() => handleAnswerSelect(option.optionId)}
                                        style={{
                                            padding: '12px 16px', marginBottom: '10px',
                                            border: isSelected ? '2px solid #3B6E91' : '2px solid #e8e8e8',
                                            borderRadius: '8px', cursor: 'pointer',
                                            background: isSelected ? '#e6f4ff' : '#fff',
                                            transition: 'border-color 0.18s, background 0.18s',
                                            display: 'flex', alignItems: 'center', gap: '12px',
                                            userSelect: 'none'
                                        }}
                                        onMouseEnter={e => { if (!isSelected) e.currentTarget.style.borderColor = '#91caff'; }}
                                        onMouseLeave={e => { if (!isSelected) e.currentTarget.style.borderColor = '#e8e8e8'; }}
                                    >
                                        <div style={{
                                            width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                                            border: isSelected ? 'none' : '2px solid #d0d0d0',
                                            background: isSelected ? '#3B6E91' : 'transparent',
                                            color: isSelected ? '#fff' : '#999',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '12px', fontWeight: 700, transition: 'all 0.18s'
                                        }}>
                                            {letter}
                                        </div>
                                        <span style={{ fontSize: '14px', color: '#333', lineHeight: '1.5' }}>{option.optionText}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div style={{
    padding: '10px 14px',
    borderTop: '1px solid #e8e8e8',
    background: '#fff',
    flexShrink: 0,
}}> 
<div style={{
        maxWidth: '800px',
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '8px',
    }}> 
    
                    {currentQuestionIndex === 0 ? (
                        <div style={{ flex: '0 0 auto', minWidth: '80px' }} />
                    ) : (
                        <button onClick={handlePrevious} style={navBtnStyle('outline')}>← Prev</button>
                    )}

                    <button
                        onClick={handleMarkForReview}
                        style={{
                            ...navBtnStyle('review'),
                            background: markReviewFlash
                                ? '#faad14'
                                : reviewQuestions.has(currentQuestion?.questionId)
                                    ? '#fff7e6'
                                    : '#fff',
                            color: '#faad14',
                            transform: markReviewFlash ? 'scale(0.95)' : 'scale(1)',
                        }}
                    >
{reviewQuestions.has(currentQuestion?.questionId)
  ? '⊙ Marked for Review'
  : '⊙ Mark for Review'}
                    </button>

                    {currentQuestionIndex === allQuestionsWithNumbers.length - 1 ? (
                        <Popconfirm
                            title=""
                            description="Are you sure you want to submit the exam?"
                            onConfirm={handleSubmit}
                            okText="Yes, Submit"
                            cancelText="Cancel"
                        >
                            <button
                                disabled={timeLeft === 0 || hasAutoSubmittedRef.current}
                                style={navBtnStyle('submit')}
                            >
                                Submit ✓
                            </button>
                        </Popconfirm>
                    ) : (
                        <button onClick={handleNext} style={navBtnStyle('primary')}>Next →</button>
                    )}
                </div>
    </div>

            </div>

            <style>{`
                .mobile-only   { display: none !important; }
                .desktop-only  { display: block !important; }
                .desktop-palette { display: flex !important; }

                @media (max-width: 768px) {
                    .desktop-palette { display: none !important; }
                    .desktop-only    { display: none !important; }
                    .mobile-only     { display: flex !important; }
                }

                @keyframes timerBlink {
                    0%, 100% { opacity: 1; }
                    50%      { opacity: 0.3; }
                }

                img {
                    -webkit-user-drag: none;
                    user-select: none;
                    pointer-events: none;
                    -webkit-tap-highlight-color: transparent;
                }

                button, div[onClick] {
                    -webkit-tap-highlight-color: transparent;
                    outline: none;
                }

                ::-webkit-scrollbar { width: 4px; }
                ::-webkit-scrollbar-thumb { background: #d0d0d0; border-radius: 4px; }
                ::-webkit-scrollbar-track { background: transparent; }
            `}</style>

            {/* ── Start Warning Modal ──
            {showStartWarning && (
                <div style={{
                    position: 'fixed', inset: 0,
                    background: 'rgba(0,0,0,0.6)', zIndex: 9998,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
                }}>
                    <div style={{
                        background: '#fff', borderRadius: '14px', padding: '32px 28px 26px',
                        maxWidth: '420px', width: '100%', textAlign: 'center',
                        boxShadow: '0 16px 48px rgba(0,0,0,0.28)'
                    }}>
                        <div style={{ fontSize: '44px', marginBottom: '12px' }}>📋</div>
                        <h3 style={{ color: '#3B6E91', fontWeight: 700, fontSize: '17px', marginBottom: '16px' }}>
                            Important Instructions
                        </h3>
                        <div style={{ textAlign: 'left', marginBottom: '22px' }}>
                            {[
                                'Do not switch tabs, apps, or windows during the exam.',
                                'Switching away will immediately auto-submit your exam.',
                                'The exam will be auto-submitted once the timer reaches zero.',
                            ].map((rule, i, arr) => {
                                const isLast = i === arr.length - 1;
                                return (
                                    <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '10px' }}>
                                        <div style={{
                                            width: '20px', height: '20px', borderRadius: '50%',
                                            background: '#ff4d4f', color: '#fff', fontSize: '11px', fontWeight: 700,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0, marginTop: '1px'
                                        }}>
                                            {isLast ? '⚠' : i + 1}
                                        </div>
                                        <span style={{
                                            fontSize: '13px', lineHeight: '1.5',
                                            fontWeight: isLast ? 600 : 400,
                                            color: isLast ? '#ff4d4f' : '#444'
                                        }}>
                                            {rule}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                        <button
                            onClick={() => setShowStartWarning(false)}
                            style={{
                                width: '100%', padding: '12px',
                                background: '#3B6E91', color: '#fff',
                                border: 'none', borderRadius: '8px',
                                fontSize: '15px', fontWeight: 600, cursor: 'pointer',
                                boxShadow: '0 4px 12px rgba(24,144,255,0.3)'
                            }}
                        >
                            I Understand — Start Exam
                        </button>
                    </div>
                </div>
            )} */}

            {showViolationWarning && (
                <div style={{
                    position: 'fixed', inset: 0,
                    background: 'rgba(0,0,0,0.75)', zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px'
                }}>
                    <div style={{
                        background: '#fff', borderRadius: '14px', padding: '40px 32px 32px',
                        maxWidth: '360px', width: '100%', textAlign: 'center',
                        boxShadow: '0 16px 48px rgba(0,0,0,0.4)'
                    }}>
                        <div style={{ fontSize: '52px', marginBottom: '14px', lineHeight: 1 }}>🚫</div>
                        <h3 style={{ color: '#ff4d4f', fontWeight: 700, fontSize: '19px', marginBottom: '10px' }}>
                            Exam Terminated
                        </h3>
                        <p style={{ color: '#555', fontSize: '14px', lineHeight: '1.7', marginBottom: '20px' }}>
                            You switched away from the exam window.<br />
                            Your exam has been <strong>automatically submitted</strong>.
                        </p>
                        <p style={{ color: '#ff4d4f', fontSize: '13px', fontWeight: 600 }}>
                            Submitting your exam…
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConductExam;